import React, { useRef, useState } from "react";
import { DescriptionOutlined, MoreHoriz } from "@mui/icons-material";
import { Menu, MenuItem } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import { invoiceTemplateActions } from "@_actions/invoiceTemplate.actions";
import { invoiceTemplateConstants } from "@_constants/invoiceTemplate.constants";

import AreYouSurePopup from "@_components/popups/AreYouSurePopup/AreYouSurePopup";
import MailPopup from "@_components/popups/MailPopup/MailPopup";

import { useConfirmPopup } from "@_hooks/useConfirmPopup";
import CardShell from "../core/CardShell";
import styles from "../CaseActivityCard.module.css";

/**
 * InvoiceActivityCard
 *
 * Renders a CASEACTIVITY_TYPE_INVOICE_GENERATED activity row.
 *
 * Actions available via the MoreHoriz menu:
 *   - View  (opens PDF viewer)
 *   - Download
 *   - Attach to email (opens compose popup)
 *   - Cancel (with confirmation)
 */

function InvoiceActivityCard({ activity, caseData, resumeAutomation }) {
  const dispatch = useDispatch();
  const caseActivityReducer = useSelector((s) => s.caseActivityReducer);
  const emailSetup = useSelector(
    (s) => s.emailSetupReducer.email_setup.active_email_setup,
  );
  const anchorRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [emailData, setEmailData] = useState({});
  const [isReplyPopupOpen, setIsReplyPopupOpen] = useState(false);

  const confirm = useConfirmPopup();

  const isCancellation =
    activity.content.type ===
    invoiceTemplateConstants.INVOICE_TYPE_CANCELLATION;
  const isCancelled =
    activity.content.type === invoiceTemplateConstants.INVOICE_TYPE_CANCELLED;
  const isActive =
    activity.content.type === invoiceTemplateConstants.INVOICE_TYPE_ACTIVE;

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleDownload = (action) => {
    dispatch(
      invoiceTemplateActions.downloadInvoice(
        activity.case_id,
        activity.content.invoice_id,
        activity.content.invoice_number,
        action,
      ),
    );
    setMenuOpen(false);
  };

  const handleAttachToEmail = () => {
    setEmailData({
      opponentEmail: caseData?.opponent_contact_details?.email,
    });
    setIsReplyPopupOpen(true);
    setMenuOpen(false);
  };

  const handleCancelPrompt = () => {
    setMenuOpen(false);
    confirm.prompt("Are you sure you want to cancel this invoice?", () => {
      confirm.close();
      dispatch(
        invoiceTemplateActions.cancelInvoice(
          activity.case_id,
          activity.content.invoice_id,
        ),
      );
    });
  };

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setMenuOpen(true);
  };

  // ── Layout pieces ──────────────────────────────────────────────────────────

  const typeText = isCancellation ? "Cancellation Invoice" : "Invoice";

  const contentPreview = (
    <>
      <div>
        {activity.content.invoice_number}
        {isCancelled ? " (Cancelled)" : ""}
      </div>
      {isCancellation && (
        <div>
          Cancelled invoice: {activity.content.cancelled_invoice_number}
        </div>
      )}
      {isCancelled && (
        <div>
          Cancellation invoice: {activity.content.cancellation_invoice_number}
        </div>
      )}
    </>
  );

  const openerColumn = (
    <>
      <MoreHoriz
        className={styles.edit_icon}
        onClick={handleMenuOpen}
        ref={anchorRef}
        aria-controls={menuOpen ? "invoice-menu" : undefined}
        aria-haspopup="true"
      />
      <Menu
        id="invoice-menu"
        open={menuOpen}
        anchorEl={anchorRef.current}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem data-testid="menu-item-view" onClick={() => handleDownload("view")}>View</MenuItem>
        <MenuItem data-testid="menu-item-download" onClick={() => handleDownload("download")}>Download</MenuItem>
        <MenuItem data-testid="menu-item-attach-to-email" onClick={handleAttachToEmail}>Attach to email</MenuItem>
        {isActive && <MenuItem data-testid="menu-item-cancel" onClick={handleCancelPrompt}>Cancel</MenuItem>}
      </Menu>
    </>
  );

  return (
    <>
      <CardShell
        activity={activity}
        icon={<DescriptionOutlined />}
        typeText={typeText}
        typeSubText={activity.user_name}
        contentPreview={contentPreview}
        openerColumn={openerColumn}
      />

      {/* Compose / attach to email popup */}
      <MailPopup
        title="New email"
        open={isReplyPopupOpen}
        close={() => {
          setIsReplyPopupOpen(false);
          setEmailData({});
        }}
        data={emailData}
        caseId={activity.case_id}
        emailSetup={emailSetup}
        resumeAutomation={resumeAutomation}
        isReplyMode={false}
        invoice={activity}
      />

      <AreYouSurePopup
        open={confirm.open}
        close={confirm.close}
        onClick={confirm.action}
        isLoading={caseActivityReducer.loading}
      >
        <p className={styles.are_you_sure_preline}>{confirm.text}</p>
      </AreYouSurePopup>
    </>
  );
}

export default InvoiceActivityCard;
